import { LOCALES } from "@/i18n/core"
import { getLang, translate } from "@/i18n"
import type { UserInfo } from "./identity"

/** Strip URL-placeholder parentheticals the LLM sometimes appends after file names. */
export function cleanLLMText(text: string): string {
  return text.replace(/\s*\([^)]*\b(URL|provided separately|see sources|in (?:your )?sources)\b[^)]*\)/gi, "")
}

const SEARCHING_LABELS: Record<string, () => string> = {
  search_projects: () => translate("searching.projects"),
  search_documents: () => "SharePoint",
  ask_work_iq: () => "Work IQ",
  tools: () => translate("searching.tools"),
}

/** Human label for the "searching" stream state. The engine (when the backend
 *  sends one for the SharePoint slot) is shown inline — the user picked it, so
 *  the spinner should say which one is actually running. */
export function searchingLabel(mode?: string, label?: string, engine?: string | null): string {
  const base = label ?? (mode ? (SEARCHING_LABELS[mode]?.() ?? "SharePoint") : "SharePoint")
  const engineName = engine ? retrievalEngineLabel(engine) : ""
  return engineName ? `${base} (${engineName})` : base
}

const ENGINE_LABELS: Record<string, () => string> = {
  graph: () => translate("engine.graph"),
  copilot: () => translate("engine.copilot"),
}

/** Short label for the engine that answered a turn. Unknown ids render as-is
 *  rather than disappearing — a new server-side engine should still be visible. */
export function retrievalEngineLabel(engine?: string): string {
  if (!engine) return ""
  return ENGINE_LABELS[engine]?.() ?? engine
}

export function formatDate(iso?: string): string {
  if (!iso) return ""
  try {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ""
    return date.toLocaleDateString(LOCALES[getLang()], { year: "numeric", month: "short", day: "numeric" })
  } catch {
    return ""
  }
}

export function firstName(account: UserInfo | null): string {
  if (!account) return ""
  if (account.name) return account.name.split(" ")[0]
  return account.username.split("@")[0]
}

/** Up to two uppercase initials from a display name or email, for avatar chips (e.g. "JM"). */
export function initials(value: string): string {
  const base = value.includes("@") ? value.split("@")[0].replace(/[._-]+/g, " ") : value
  const parts = base.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return "?"
  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ""
  return (first + last).toUpperCase()
}
